// The custom fields a tenant defines, and their values on its rows: the schemas, the values, a
// field's options, and the one write — an asset's values.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type MetadataSchemaView = components['schemas']['MetadataSchemaView']
export type MetadataFieldEntry = components['schemas']['MetadataFieldEntry']
export type MetadataValueEntry = components['schemas']['MetadataValueEntry']
export type FieldChoiceEntry = components['schemas']['FieldChoiceEntry']
export type UpdateMetadataValuesRequest = components['schemas']['UpdateMetadataValuesRequest']
export type MetadataValueUpdate = components['schemas']['MetadataValueUpdate']
export type MetadataSchemasQuery = NonNullable<operations['metadata_schemas_list']['parameters']['query']>
export type MetadataValuesQuery = NonNullable<operations['metadata_values_list']['parameters']['query']>
export type FieldChoicesQuery = NonNullable<operations['metadata_field_choices_list']['parameters']['query']>

/**
 * The schemas that apply, in display order, each with its live active fields. Without a
 * `workspace` the answer is the organization-wide schemas only; with one, those and that
 * workspace's.
 */
export function listMetadataSchemas(
  query: MetadataSchemasQuery = {},
  signal?: AbortSignal,
): Promise<MetadataSchemaView[]> {
  return api.get<MetadataSchemaView[]>(withQuery('/metadata/schemas', query), undefined, signal)
}

/**
 * The live values of the rows `ids` names — comma-separated, at most 500 — ordered as a form lays
 * them out. A row with no value contributes nothing, so an empty array is an ordinary answer.
 */
export function listMetadataValues(query: MetadataValuesQuery, signal?: AbortSignal): Promise<MetadataValueEntry[]> {
  return api.get<MetadataValueEntry[]>(withQuery('/metadata/values', query), undefined, signal)
}

/**
 * The options a `choice` or `model_choice` field offers: the uids in `selected` first and whatever
 * `search` says, then up to `limit` more. Empty for a field of any other type.
 */
export function listMetadataFieldChoices(
  fieldId: string,
  query: FieldChoicesQuery = {},
  signal?: AbortSignal,
): Promise<FieldChoiceEntry[]> {
  return api.get<FieldChoiceEntry[]>(
    withQuery(`/metadata/fields/${encodeURIComponent(fieldId)}/choices`, query),
    undefined,
    signal,
  )
}

/**
 * Change some of an asset's custom fields — all of them or none — and answer every value the
 * asset now carries. A field not named keeps its value; `null` empties one.
 */
export function updateAssetMetadataValues(
  assetId: string,
  request: UpdateMetadataValuesRequest,
): Promise<MetadataValueEntry[]> {
  return api.patch<MetadataValueEntry[]>(`/assets/${encodeURIComponent(assetId)}/metadata-values`, request)
}
